import React, { useEffect, useState, useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { DetailStyle, InfoColumn } from './CampaignDetails.styles.js';
import dashboardService from '../../../services/dashboardService';
import { useContextHook } from 'use-context-hook';
import { AuthContext } from '../../../Context/authContext';
import { useLocation } from 'react-router-dom';
import campaignService from '../../../services/campaignService.js';
import Toast from '../../molecules/Toast/index.jsx';

const CampaignDetails = ({ hasPadding, isEdit, searchQuery, graphDataFromParent }) => {
  const location = useLocation();
  const campaignId = location.state?.campaign_id;
  const [analyticsData, setAnalyticsData] = useState();
  const { fetch } = useContextHook(AuthContext, v => ({
    fetch: v.fetch,
  }));

  // const { kpi_data, kpi_loading } = dashboardService.GetKPIAnalytics(isEdit ? null : searchQuery, fetch);

  const num = n => {
    try {
      return new Intl.NumberFormat().format(Number(n));
    } catch (e) {
      return n;
    }
  };

  const calculatedTotals = useMemo(() => {
    if (!graphDataFromParent) return null;

    const sumValues = key => {
      return graphDataFromParent[key]?.reduce((sum, val) => sum + (Number(val) || 0), 0) || 0;
    };

    return {
      emailSent: sumValues('emailsSent'),
      emailOpened: sumValues('emailsOpened'),
      replies: sumValues('replies'),
      clicks: sumValues('clicks'),
      positiveReply: sumValues('positivereply'),
      opportunity: sumValues('opportunity'),
      unsubscribed: sumValues('unsubscribed'),
      bounced: sumValues('bounced'),
    };
  }, [graphDataFromParent]);

  const emailOpened = calculatedTotals?.emailOpened || 0;
  const emailSent = calculatedTotals?.emailSent || 0;

  const graphDataMap = useMemo(() => {
    if (!graphDataFromParent) return null;

    const filterToActiveMonths = dataArray => {
      if (!dataArray || !Array.isArray(dataArray)) {
        // Return a flat line (straight line) when no data
        return [0, 0, 0, 0, 0, 0];
      }

      // Check if all values are zero or empty (no data)
      const hasData = dataArray.some(val => Number(val) > 0);

      if (!hasData) {
        // Return a flat line (straight line) when all values are zero
        const flatLineLength = Math.max(dataArray.length, 6); // Ensure minimum 6 points for visibility
        return new Array(flatLineLength).fill(0);
      }

      const firstNonZeroIndex = dataArray.findIndex(val => Number(val) > 0);
      if (firstNonZeroIndex === -1) {
        // Fallback: return flat line if somehow no data found
        const flatLineLength = Math.max(dataArray.length, 6);
        return new Array(flatLineLength).fill(0);
      }

      const lastNonZeroIndex = dataArray
        .map((val, idx) => (Number(val) > 0 ? idx : -1))
        .reduce((max, idx) => (idx > max ? idx : max), -1);

      const startIndex = Math.max(0, firstNonZeroIndex - 1);
      const endIndex = Math.min(dataArray.length, lastNonZeroIndex + 2);

      const slicedData = dataArray.slice(startIndex, endIndex);

      // Ensure we have at least 2 data points for a proper line graph
      // If we only have 1 point (e.g., today's data), pad with zeros before it
      if (slicedData.length === 1) {
        return [0, ...slicedData];
      }

      // Ensure minimum 2 points for graph visibility
      if (slicedData.length < 2) {
        return [0, 0];
      }

      return slicedData;
    };

    return {
      emailsSent: filterToActiveMonths(graphDataFromParent.emailsSent),
      emailsOpened: filterToActiveMonths(graphDataFromParent.emailsOpened),
      replies: filterToActiveMonths(graphDataFromParent.replies),
      clicks: filterToActiveMonths(graphDataFromParent.clicks),
      positivereply: filterToActiveMonths(graphDataFromParent.positivereply),
      opportunity: filterToActiveMonths(graphDataFromParent.opportunity),
      unsubscribed: filterToActiveMonths(graphDataFromParent.unsubscribed),
      bounced: filterToActiveMonths(graphDataFromParent.bounced),
    };
  }, [graphDataFromParent]);

  const details = [
    {
      subtitle: 'Email Send',
      // value: '1,000',
      value: emailSent || 0,
      text: (
        <>
          <span className="green">↑500</span> than last day
        </>
      ),
      data: graphDataMap?.emailsSent,
      graphColor: '#0DA06A',
      areaType: true,
      hasPadding: false,
    },
    {
      subtitle: 'Email Opened',
      // value: '1,000',
      value: emailOpened || 0,

      text: (
        <>
          <span className="green">↑150</span> than last day
        </>
      ),
      data: graphDataMap?.emailsOpened,
      graphColor: '#0DA06A',
      areaType: true,
      hasPadding: false,
    },
    {
      subtitle: 'Email Replies',
      // value: '1,000',
      value: calculatedTotals?.replies || 0,

      text: (
        <>
          <span className="green">↑500</span> than last day
        </>
      ),
      data: graphDataMap?.replies,
      graphColor: '#0DA06A',
      areaType: true,
      hasPadding: false,
    },
    {
      subtitle: 'Click Rate',
      // value: '1,000',
      value: calculatedTotals?.clicks || 0,

      text: (
        <>
          <span className="green">↑150</span> than last day
        </>
      ),
      data: graphDataMap?.clicks,
      graphColor: '#0DA06A',
      areaType: true,
      hasPadding: false,
    },
    {
      subtitle: 'Positive Replies',
      value: calculatedTotals?.positiveReply || 0,
      data: graphDataMap?.positivereply,
      graphBg: '#E4FFF6',
      graphColor: '#28CF57',
      areaType: false,
      hasPadding: true,
      height: true,
    },
    {
      subtitle: 'Opportunities',
      value: calculatedTotals?.opportunity || 0,
      data: graphDataMap?.opportunity,
      graphBg: '#E4FFF6',
      graphColor: '#28CF57',
      areaType: false,
      hasPadding: true,
      height: true,
    },
    {
      subtitle: 'Unsubscribed',
      value: calculatedTotals?.unsubscribed || 0,
      data: graphDataMap?.unsubscribed,
      graphBg: 'rgba(255, 1, 10, 0.1)',
      graphColor: '#FF010A',
      areaType: false,
      hasPadding: true,
      height: true,
    },
    {
      subtitle: 'Bounced',
      // value: '12',
      value: calculatedTotals?.bounced || 0,
      data: graphDataMap?.bounced || 0,
      graphBg: 'rgba(255, 1, 10, 0.1)',
      graphColor: '#FF010A',
      areaType: false,
      hasPadding: true,
      height: true,
    },
  ];

  // Chart Options Flexible type
  const getChartOptions = (data, graphColor, areaType) => {
    // Ensure data is always an array
    const chartData = Array.isArray(data) ? data : [0, 0, 0, 0, 0, 0];

    return {
      chart: {
        type: areaType ? 'areaspline' : 'spline',
        backgroundColor: 'transparent',
        height: 55,
        spacing: [5, 5, 5, 5],
        marginLeft: 3,
        marginRight: 3,
        marginTop: 3,
        marginBottom: 3,
      },
      title: { text: null },
      xAxis: {
        visible: false,
        lineWidth: 0,
      },
      yAxis: {
        visible: false,
        startOnTick: false,
        endOnTick: false,
        maxPadding: 0.12,
        minPadding: 0.12,
        min: 0, // Ensure y-axis starts at 0 for proper straight line display
      },
      legend: { enabled: false },
      credits: { enabled: false },
      tooltip: { enabled: false },
      plotOptions: {
        series: {
          animation: {
            duration: 1000,
            easing: 'easeOutQuart',
          },
          marker: { enabled: false },
          lineWidth: 1.8,
          states: {
            hover: { enabled: false },
            inactive: { enabled: false },
          },
        },
        spline: {
          lineWidth: 1.8,
          marker: { enabled: false },
        },
        areaspline: {
          lineWidth: 1.8,
          marker: { enabled: false },
          fillColor: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0, graphColor],
              [1, 'rgba(255,255,255,0)'],
            ],
          },
        },
      },
      series: [
        {
          data: chartData,
          color: graphColor,
        },
      ],
    };
  };

  return (
    <DetailStyle>
      {details?.map((item, index) => (
        <InfoColumn key={index} hasPadding={item.hasPadding} height={item.height}>
          <strong className="subtitle">{item.subtitle}</strong>
          <div className="wrap">
            <div className="text-box">
              <strong className="value">{item.value}</strong>
              {/* {item.text && <span className="text">{item.text}</span>} */}
            </div>

            <div className="graph-box" style={{ backgroundColor: item.graphBg || 'transparent' }}>
              <HighchartsReact
                highcharts={Highcharts}
                options={getChartOptions(item.data, item.graphColor, item.areaType)}
              />
            </div>
          </div>
        </InfoColumn>
      ))}
    </DetailStyle>
  );
};

export default CampaignDetails;
